/**
 * Date Role - Cast Pillar
 *
 * Normaliza input sujo para o tipo esperado de cada variante.
 *
 * @example
 * import { castDate, castIso, castTimestamp, castEpoch } from '@attrx/role-morphic/date/cast';
 *
 * castDate('iso', '2024-12-05');                // '2024-12-05T00:00:00.000Z'
 * castDate('timestamp', '2024-12-05');          // 1733356800000
 * castDate('epoch', new Date());                // 1733425800
 */

import type { DateVariant, DateIso, DateTimestamp, DateEpoch } from "./types";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

// =============================================================================
// CAST ISO
// =============================================================================

/**
 * Faz cast de input para ISO string
 *
 * @example
 * castIso('2024-12-05');           // '2024-12-05T00:00:00.000Z'
 * castIso(1733425800000);          // '2024-12-05T19:30:00.000Z'
 * castIso(new Date());             // ISO string da data atual
 */
export function castIso(input: unknown): DateIso | null {
  // Já é string
  if (typeof input === "string") {
    const trimmed = input.trim();
    const date = new Date(trimmed);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
    return null;
  }

  // Número (timestamp)
  if (typeof input === "number") {
    if (!Number.isFinite(input)) return null;
    const date = new Date(input);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
    return null;
  }

  // Date object
  if (input instanceof Date) {
    if (isNaN(input.getTime())) return null;
    return input.toISOString();
  }

  return null;
}

// =============================================================================
// CAST TIMESTAMP
// =============================================================================

/**
 * Faz cast de input para timestamp (milissegundos)
 *
 * @example
 * castTimestamp('2024-12-05T19:30:00.000Z');  // 1733425800000
 * castTimestamp('1733425800000');             // 1733425800000
 * castTimestamp(new Date());                  // timestamp atual
 */
export function castTimestamp(input: unknown): DateTimestamp | null {
  // Já é número
  if (typeof input === "number") {
    if (!Number.isFinite(input)) return null;
    return input;
  }

  // String
  if (typeof input === "string") {
    const trimmed = input.trim();

    // String numérica pura
    if (/^-?\d+$/.test(trimmed)) {
      const num = parseInt(trimmed, 10);
      if (Number.isFinite(num)) return num;
    }

    // Tenta parsear como ISO date
    const date = new Date(trimmed);
    if (!isNaN(date.getTime())) {
      return date.getTime();
    }

    return null;
  }

  // Date object
  if (input instanceof Date) {
    const ts = input.getTime();
    return isNaN(ts) ? null : ts;
  }

  return null;
}

// =============================================================================
// CAST EPOCH
// =============================================================================

/**
 * Faz cast de input para epoch (segundos)
 *
 * Detecta automaticamente se o input é milissegundos (> 1e11) e converte.
 *
 * @example
 * castEpoch('2024-12-05T19:30:00.000Z');  // 1733425800
 * castEpoch(1733425800000);               // 1733425800 (detecta ms)
 * castEpoch(1733425800);                  // 1733425800
 * castEpoch(new Date());                  // epoch atual
 */
export function castEpoch(input: unknown): DateEpoch | null {
  // Já é número
  if (typeof input === "number") {
    if (!Number.isFinite(input)) return null;
    // Se parece ser milissegundos (> 1e11), converte para segundos
    if (Math.abs(input) > 1e11) {
      return Math.floor(input / 1000);
    }
    return Math.floor(input);
  }

  // String
  if (typeof input === "string") {
    const trimmed = input.trim();

    // String numérica pura
    if (/^-?\d+$/.test(trimmed)) {
      const num = parseInt(trimmed, 10);
      if (Number.isFinite(num)) {
        // Se parece ser milissegundos (> 1e11), converte para segundos
        if (Math.abs(num) > 1e11) {
          return Math.floor(num / 1000);
        }
        return num;
      }
    }

    // Tenta parsear como ISO date e converter
    const date = new Date(trimmed);
    if (!isNaN(date.getTime())) {
      return Math.floor(date.getTime() / 1000);
    }

    return null;
  }

  // Date object
  if (input instanceof Date) {
    const ts = input.getTime();
    return isNaN(ts) ? null : Math.floor(ts / 1000);
  }

  return null;
}

// =============================================================================
// MAIN CAST
// =============================================================================

/**
 * Faz cast de input para uma variante de data
 *
 * @param variant - Variante de destino ("iso", "timestamp", "epoch")
 * @param input - Valor de entrada
 * @returns Valor convertido ou null se não for possível
 *
 * @example
 * castDate('iso', '2024-12-05');      // '2024-12-05T00:00:00.000Z'
 * castDate('timestamp', new Date());  // timestamp atual
 * castDate('epoch', '2024-12-05');    // 1733356800
 */
export function castDate<T = unknown>(variant: DateVariant, input: unknown): T | null {
  switch (variant) {
    case "iso":
      return castIso(input) as T | null;
    case "timestamp":
      return castTimestamp(input) as T | null;
    case "epoch":
      return castEpoch(input) as T | null;
    default:
      return null;
  }
}

/**
 * Versão safe de castDate que retorna Result
 */
export function tryCastDate<T = unknown>(
  variant: DateVariant,
  input: unknown
): Result<T> {
  const result = castDate<T>(variant, input);
  if (result === null) {
    return {
      ok: false,
      error: `Cannot cast "${String(input)}" to date variant "${variant}"`,
    };
  }
  return { ok: true, value: result };
}

/**
 * Detecta se um valor parece ser um timestamp em milissegundos ou segundos
 *
 * @returns "timestamp" se > 1e11, "epoch" caso contrário, null se não for número
 */
export function detectDateNumericVariant(value: unknown): "timestamp" | "epoch" | null {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }

  // Timestamps em ms geralmente são > 1e12 (após ano 2001)
  // Epochs em segundos são tipicamente < 1e11
  return Math.abs(value) > 1e11 ? "timestamp" : "epoch";
}
